import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

const PaymentSuccessModal = ({ sessionId, onClose }) => {
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return;

    const fetchPaymentDetails = async () => {
      setLoading(true);
      setError(null);
      setPaymentDetails(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-payment-details`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch payment details');
        }

        setPaymentDetails(data);
      } catch (fetchError) {
        setError(fetchError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [sessionId]);

  if (!sessionId) return null;

  const detailRows = paymentDetails
    ? [
        {
          label: 'Amount Paid',
          value: `$${((paymentDetails.price || 0) / 100).toFixed(2)}`,
          highlight: true,
        },
        {
          label: 'Payment ID',
          value: paymentDetails.reference_no || 'N/A',
        },
        {
          label: 'Status',
          value: paymentDetails.payment_status || 'N/A',
        },
        ...(paymentDetails.payment_method
          ? [{ label: 'Payment Method', value: paymentDetails.payment_method }]
          : []),
        ...(paymentDetails.card_last4
          ? [{ label: 'Card', value: `**** **** **** ${paymentDetails.card_last4}` }]
          : []),
      ]
    : [];

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-4 backdrop-blur-[3px] sm:py-6">
      <div
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-success-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-xl border border-slate-200 p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 sm:right-4 sm:top-4"
          aria-label="Close"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>

        {loading ? (
          <div className="px-6 py-12 text-center sm:px-8">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="mt-4 text-sm text-slate-600">Loading payment details...</p>
          </div>
        ) : error ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-7 text-center sm:px-8 sm:pt-8">
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-red-50 ring-1 ring-red-200 sm:h-12 sm:w-12">
                <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
              </span>
              <h3
                id="payment-success-title"
                className="mt-3 text-md font-medium uppercase tracking-[0.04em] text-slate-900 sm:mt-4"
              >
                Payment Error
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{error}</p>
            </div>
            <div className="shrink-0 border-t border-slate-200 bg-slate-50/70 px-6 py-3 sm:px-8 sm:py-4">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:py-3"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="px-6 pt-7 text-center sm:px-8 sm:pt-8">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 sm:h-12 sm:w-12">
                  <CheckCircleIcon className="h-6 w-6" />
                </span>
                <h3
                  id="payment-success-title"
                  className="mt-3 text-md font-medium uppercase tracking-[0.04em] text-slate-900 sm:mt-4"
                >
                  Payment Confirmed
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-slate-600 sm:mt-2">
                  Thank you for your payment.
                </p>
              </div>

              <div className="mt-4 px-6 pb-1 sm:mt-5 sm:px-8">
                <h4 className="mb-2.5 text-xs font-medium uppercase tracking-[0.08em] text-slate-900 sm:mb-3">
                  Payment Details
                </h4>
                <div className="space-y-2">
                  {detailRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between gap-3 rounded-[0.85rem] border border-slate-200 bg-slate-50/80 px-3 py-2.5"
                    >
                      <span className="shrink-0 text-xs text-slate-600 sm:text-sm">{row.label}</span>
                      <span
                        className={`min-w-0 truncate text-right text-xs font-medium sm:text-sm ${
                          row.highlight ? 'text-base font-bold text-blue-600' : 'text-slate-900'
                        }`}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-2 border-t border-slate-200 bg-slate-50/70 px-6 py-3 sm:px-8 sm:py-4">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/dashboard');
                }}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-transparent bg-blue-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:py-3"
              >
                Back to Dashboard
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/order-history');
                }}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:py-3"
              >
                <ClipboardDocumentListIcon className="h-4 w-4 text-blue-600" />
                View Order History
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:py-3"
              >
                Stay on Cart
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PaymentSuccessModal;
